import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from 'configuration/paths';
import { TranslationsKeys } from 'types/translation';
import { Status } from 'types/surveyResponse';
import { useTranslate } from 'hooks/useTranslate';
import { useAuthentication } from 'hooks/useAuthentication';
import { useCustomerVisitsQuery, useSurveyDefinitionsQuery, useSurveyResponsesQuery } from 'hooks/queries';
import { Header } from 'components/Header';
import { MessageBar } from 'components/MessageBar';
import { Tabs } from 'components/ui/Tabs';
import { PlannedVisits } from './components/PlannedVisits';
import { SurveyResponses } from './components/SurveyResponses';
import styles from './MySurveys.module.scss';
import { AlertCircle } from 'lucide-react';

type MySurveysTab = {
  key:
    | TranslationsKeys.mySurveysPage_tabs_plannedVisits
    | TranslationsKeys.mySurveysPage_tabs_inProgressVisits
    | TranslationsKeys.mySurveysPage_tabs_completedVisits;
  path: string;
  name: string;
};

const MySurveys = (): JSX.Element => {
  const { t } = useTranslate();
  const { user } = useAuthentication();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showSubmittedMessage, setShowSubmittedMessage] = useState(false);
  const customerVisitsQuery = useCustomerVisitsQuery(user?.email ?? ''); // should not be empty
  const surveyDefinitionsQuery = useSurveyDefinitionsQuery();
  const surveyResponsesQuery = useSurveyResponsesQuery();
  const numCustomerVisits = (customerVisitsQuery.data || []).length;
  const numInProgressVisits = useMemo(() => (surveyResponsesQuery.data || []).filter(sr => sr.status === Status.NotStarted || sr.status === Status.InProgress).length,
    [surveyResponsesQuery.data]);
  const numCompletedVisits = useMemo(() => (surveyResponsesQuery.data || []).filter(sr => sr.status === Status.Complete).length,
    [surveyResponsesQuery.data]);
  const isError = customerVisitsQuery.error || surveyResponsesQuery.error || surveyDefinitionsQuery.error;
  
  const tabs = [
    {
      key: TranslationsKeys.mySurveysPage_tabs_plannedVisits,
      path: paths.mySurveys_planned,
      name: `${t(TranslationsKeys.mySurveysPage_tabs_plannedVisits)} (${numCustomerVisits > 99 ? '99+' : numCustomerVisits})`
    },
    {
      key: TranslationsKeys.mySurveysPage_tabs_inProgressVisits,
      path: paths.mySurveys_inProgress,
      name: `${t(TranslationsKeys.mySurveysPage_tabs_inProgressVisits)} (${numInProgressVisits > 99 ? '99+' : numInProgressVisits})`
    },
    {
      key: TranslationsKeys.mySurveysPage_tabs_completedVisits,
      path: paths.mySurveys_completed,
      name: `${t(TranslationsKeys.mySurveysPage_tabs_completedVisits)} (${numCompletedVisits > 99 ? '99+' : numCompletedVisits})`
    }
  ] as MySurveysTab[];
  const [activeTab, setActiveTab] = useState<MySurveysTab>(tabs.find((tab) => tab.path === pathname) ?? tabs[0]);
  
  useEffect(() => {
    setActiveTab(tabs.find((tab) => tab.path === pathname) ?? tabs[0]);
  }, [pathname]);

  useEffect(() => {
    if (searchParams.get('submitted') && !showSubmittedMessage) {
      setShowSubmittedMessage(true);
      setTimeout(() => {
        setShowSubmittedMessage(false);
        setSearchParams({});
      }, 5000);
    }
  }, [searchParams.get('submitted'), showSubmittedMessage]);

  return (
    <div className={styles.mySurveysWrapper}>
      <Header />
      <div className={styles.mySurveys}>
        <div className={styles.mySurveysTabs}>
          <Tabs
            items={tabs.map((tab) => ({ text: tab.name, selected: tab.key === activeTab.key }))}
            onSelect={(tabIndex: number) => navigate(tabs[tabIndex].path)}
          />
        </div>
        {isError && <div className={styles.mySurveysError}><AlertCircle /> {t(TranslationsKeys.mySurveysPage_error)}</div>}
        {activeTab.key === TranslationsKeys.mySurveysPage_tabs_plannedVisits && (
          <div className={styles.mySurveysPlanned}>
            <PlannedVisits visits={customerVisitsQuery.data || []} loading={customerVisitsQuery.isLoading} />
          </div>
        )}
        {activeTab.key === TranslationsKeys.mySurveysPage_tabs_inProgressVisits && (
          <div className={styles.mySurveysInProgress}>
            <SurveyResponses
              isCompleted={false}
              surveyResponses={surveyResponsesQuery.data || []}
              surveyDefinitions={surveyDefinitionsQuery.data || []}
              loading={surveyResponsesQuery.isLoading || surveyDefinitionsQuery.isLoading} />
          </div>
        )}
        {activeTab.key === TranslationsKeys.mySurveysPage_tabs_completedVisits && (
          <div className={styles.mySurveysCompleted}>
            <SurveyResponses
              isCompleted={true}
              surveyResponses={surveyResponsesQuery.data || []}
              surveyDefinitions={surveyDefinitionsQuery.data || []}
              loading={surveyResponsesQuery.isLoading || surveyDefinitionsQuery.isLoading} />
          </div>
        )}
      </div>

      {showSubmittedMessage && (
        <div className={styles.mySurveysMessageBar}>
          <MessageBar
            variant="success"
            title=""
            showCloseAction={false}
            fixed
            description={t(TranslationsKeys.surveyWrapUpPage_submitted)}
          />
        </div>
      )}
    </div>
  );
};

export default MySurveys;
